import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit2, Trash2, Gift, Package, Clock, CheckCircle, XCircle, Sparkles, Upload, X, Image, Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { AIGenerateButton, AIGenerateModal, AIGenerateContext } from '@/components/ai';
import { useAIGenerate } from '@/hooks/useAIGenerate';

interface RewardsCatalogProps {
  companyId: string;
}

interface RewardFormData {
  name: string;
  description: string;
  points_required: number;
  quantity_available: number | null;
  image_url: string;
  is_active: boolean;
}

const defaultReward: RewardFormData = {
  name: '',
  description: '',
  points_required: 100,
  quantity_available: null,
  image_url: '',
  is_active: true,
};

const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB

export function RewardsCatalog({ companyId }: RewardsCatalogProps) {
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingReward, setEditingReward] = useState<any>(null);
  const [formData, setFormData] = useState<RewardFormData>(defaultReward);
  
  // Image upload state
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const imageInputRef = useRef<HTMLInputElement>(null);

  // AI Generation state
  const { generate: aiGenerate, isLoading: aiLoading } = useAIGenerate();
  const [aiModalOpen, setAiModalOpen] = useState<'reward_name' | 'reward_description' | null>(null);

  const getAIContext = (): AIGenerateContext[] => {
    const context: AIGenerateContext[] = [];
    context.push({ key: 'points', label: 'Points', value: String(formData.points_required) });
    if (formData.name && aiModalOpen === 'reward_description') {
      context.push({ key: 'name', label: 'Reward', value: formData.name });
    }
    return context;
  };

  const handleAIGenerate = async (customPrompt?: string): Promise<string[]> => {
    if (!aiModalOpen) return [];
    return aiGenerate({
      field: aiModalOpen,
      context: {
        pointsRequired: String(formData.points_required),
        rewardName: formData.name,
        category: 'corporate training',
      },
      customPrompt,
      variations: 3,
      category: 'gamification',
    });
  };

  const applyAIValue = (value: string) => {
    if (aiModalOpen === 'reward_name') {
      setFormData({ ...formData, name: value });
    } else if (aiModalOpen === 'reward_description') {
      setFormData({ ...formData, description: value });
    }
    setAiModalOpen(null);
  };

  // Fetch rewards
  const { data: rewards = [], isLoading: rewardsLoading } = useQuery({
    queryKey: ['company-rewards', companyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('company_rewards')
        .select('*')
        .eq('company_id', companyId)
        .order('points_required', { ascending: true });
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!companyId,
  });

  // Fetch redemptions
  const { data: redemptions = [], isLoading: redemptionsLoading } = useQuery({
    queryKey: ['reward-redemptions', companyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_reward_redemptions')
        .select(`
          *,
          profiles(display_name, email),
          company_rewards(name)
        `)
        .eq('company_id', companyId)
        .order('redeemed_at', { ascending: false });
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!companyId,
  });

  // Create reward
  const createMutation = useMutation({
    mutationFn: async (data: RewardFormData) => {
      const { error } = await supabase
        .from('company_rewards')
        .insert({
          company_id: companyId,
          ...data,
          display_order: rewards.length,
        });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-rewards', companyId] });
      toast.success('Reward created successfully');
      resetForm();
    },
    onError: (error: any) => {
      toast.error('Failed to create reward: ' + error.message);
    },
  });

  // Update reward
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: RewardFormData }) => {
      const { error } = await supabase
        .from('company_rewards')
        .update(data)
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-rewards', companyId] });
      toast.success('Reward updated successfully');
      resetForm();
    },
    onError: (error: any) => {
      toast.error('Failed to update reward: ' + error.message);
    },
  });

  // Delete reward
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('company_rewards')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-rewards', companyId] });
      toast.success('Reward deleted');
    },
    onError: (error: any) => {
      toast.error('Failed to delete reward: ' + error.message);
    },
  });

  // Update redemption status
  const updateRedemptionMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const updateData: any = { status };
      if (status === 'fulfilled') {
        updateData.fulfilled_at = new Date().toISOString();
      }
      
      const { error } = await supabase
        .from('user_reward_redemptions')
        .update(updateData)
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reward-redemptions', companyId] });
      toast.success('Redemption status updated');
    },
    onError: (error: any) => {
      toast.error('Failed to update redemption: ' + error.message);
    },
  });

  const resetForm = () => {
    setFormData(defaultReward);
    setEditingReward(null);
    setIsDialogOpen(false);
    setImagePreview(null);
  };

  const handleEdit = (reward: any) => {
    setEditingReward(reward);
    setFormData({
      name: reward.name,
      description: reward.description,
      points_required: reward.points_required,
      quantity_available: reward.quantity_available,
      image_url: reward.image_url || '',
      is_active: reward.is_active,
    });
    setImagePreview(reward.image_url || null);
    setIsDialogOpen(true);
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      toast.error('Please upload a JPG, PNG, or WEBP image');
      return;
    }

    // Validate file size
    if (file.size > MAX_IMAGE_SIZE) {
      toast.error('Image must be less than 10MB');
      return;
    }

    try {
      setIsUploading(true);

      // Create preview
      const reader = new FileReader();
      reader.onload = (event) => {
        setImagePreview(event.target?.result as string);
      };
      reader.readAsDataURL(file);

      // Upload to Supabase storage
      const fileExt = file.name.split('.').pop();
      const fileName = `reward-${companyId}-${Date.now()}.${fileExt}`;
      const filePath = `rewards/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('agent-profile-images')
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('agent-profile-images')
        .getPublicUrl(filePath);

      setFormData(prev => ({ ...prev, image_url: publicUrl }));
      toast.success('Image uploaded successfully');
    } catch (error: any) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image: ' + error.message);
      setImagePreview(null);
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({ ...prev, image_url: '' }));
    setImagePreview(null);
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingReward) {
      updateMutation.mutate({ id: editingReward.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="gap-1"><Clock className="h-3 w-3" /> Pending</Badge>;
      case 'approved':
        return <Badge variant="secondary" className="gap-1"><CheckCircle className="h-3 w-3" /> Approved</Badge>;
      case 'fulfilled':
        return <Badge variant="default" className="gap-1"><CheckCircle className="h-3 w-3" /> Fulfilled</Badge>;
      case 'rejected':
        return <Badge variant="destructive" className="gap-1"><XCircle className="h-3 w-3" /> Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const pendingCount = redemptions.filter((r: any) => r.status === 'pending').length;

  return (
    <div className="space-y-6">
      <Tabs defaultValue="catalog">
        <div className="flex items-center justify-between mb-4">
          <TabsList>
            <TabsTrigger value="catalog">Rewards Catalog</TabsTrigger>
            <TabsTrigger value="redemptions" className="gap-2">
              Redemptions
              {pendingCount > 0 && (
                <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 text-xs">
                  {pendingCount}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => { resetForm(); setIsDialogOpen(true); }} className="gap-2">
                <Plus className="h-4 w-4" />
                Add Reward
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingReward ? 'Edit Reward' : 'Create New Reward'}</DialogTitle>
                <DialogDescription>
                  Define a redeemable reward for your team
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label>Reward Name</Label>
                    <AIGenerateButton onClick={() => setAiModalOpen('reward_name')} disabled={aiLoading} />
                  </div>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Company Swag Pack"
                    required
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label>Description</Label>
                    <AIGenerateButton onClick={() => setAiModalOpen('reward_description')} disabled={aiLoading} />
                  </div>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="What's included in this reward..."
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Points Required</Label>
                    <Input
                      type="number"
                      value={formData.points_required}
                      onChange={(e) => setFormData({ ...formData, points_required: parseInt(e.target.value) || 0 })}
                      min={1}
                      required
                    />
                  </div>
                  <div>
                    <Label>Quantity Available</Label>
                    <Input
                      type="number"
                      value={formData.quantity_available || ''}
                      onChange={(e) => setFormData({ ...formData, quantity_available: e.target.value ? parseInt(e.target.value) : null })}
                      placeholder="Unlimited"
                      min={0}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Reward Image (optional)</Label>
                  <input
                    ref={imageInputRef}
                    type="file"
                    accept=".jpg,.jpeg,.png,.webp"
                    onChange={handleImageUpload}
                    className="hidden"
                    id="reward-image-upload"
                  />
                  
                  {imagePreview ? (
                    <div className="relative group">
                      <img 
                        src={imagePreview} 
                        alt="Reward preview" 
                        className="w-full h-32 object-cover rounded-lg border"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="absolute top-2 right-2 h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={handleRemoveImage}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <label
                      htmlFor="reward-image-upload"
                      className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer hover:bg-muted/50 transition-colors"
                    >
                      {isUploading ? (
                        <div className="flex flex-col items-center gap-2">
                          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">Uploading...</span>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-2">
                          <Upload className="h-8 w-8 text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">Click to upload image</span>
                          <span className="text-xs text-muted-foreground">JPG, PNG, WEBP (max 5MB)</span>
                        </div>
                      )}
                    </label>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  />
                  <Label>Active</Label>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={resetForm} className="flex-1">
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    className="flex-1"
                    disabled={createMutation.isPending || updateMutation.isPending}
                  >
                    {editingReward ? 'Update Reward' : 'Create Reward'}
                  </Button>
                </div>
              </form>

              {/* AI Generation Modal */}
              {aiModalOpen && (
                <AIGenerateModal
                  open={!!aiModalOpen}
                  onOpenChange={(open) => !open && setAiModalOpen(null)}
                  title={aiModalOpen === 'reward_name' ? 'Reward Name' : 'Reward Description'}
                  fieldName={aiModalOpen}
                  context={getAIContext()}
                  onGenerate={handleAIGenerate}
                  onApply={applyAIValue}
                  isLoading={aiLoading}
                  multiline={aiModalOpen === 'reward_description'}
                />
              )}
            </DialogContent>
          </Dialog>
        </div>

        <TabsContent value="catalog">
          {rewardsLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3].map(i => (
                <Card key={i} className="animate-pulse">
                  <CardContent className="h-48" />
                </Card>
              ))}
            </div>
          ) : rewards.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Gift className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">No rewards created yet</p>
                <p className="text-sm text-muted-foreground mt-1">Add rewards that team members can redeem with CPD points</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {rewards.map((reward: any) => (
                <Card key={reward.id} className={!reward.is_active ? 'opacity-60' : ''}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        {reward.image_url ? (
                          <img src={reward.image_url} alt={reward.name} className="w-12 h-12 rounded-lg object-cover" />
                        ) : (
                          <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                            <Gift className="h-6 w-6 text-primary" />
                          </div>
                        )}
                        <div>
                          <CardTitle className="text-lg">{reward.name}</CardTitle>
                          <Badge variant={reward.is_active ? 'default' : 'secondary'} className="mt-1">
                            {reward.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(reward)}>
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => deleteMutation.mutate(reward.id)}
                          disabled={deleteMutation.isPending}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-sm text-muted-foreground">{reward.description}</p>
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-1">
                        <span className="font-bold text-primary">{reward.points_required}</span>
                        <span className="text-muted-foreground">points</span>
                      </div>
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <Package className="h-4 w-4" />
                        <span>
                          {reward.quantity_available === null 
                            ? 'Unlimited' 
                            : `${reward.quantity_available - (reward.quantity_claimed || 0)} left`}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="redemptions">
          <Card>
            <CardHeader>
              <CardTitle>Reward Redemptions</CardTitle>
              <CardDescription>Manage pending and completed reward claims</CardDescription>
            </CardHeader>
            <CardContent>
              {redemptionsLoading ? (
                <div className="animate-pulse space-y-4">
                  {[1, 2, 3].map(i => <div key={i} className="h-12 bg-muted rounded" />)}
                </div>
              ) : redemptions.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No redemptions yet
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Reward</TableHead>
                      <TableHead>Points</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {redemptions.map((redemption: any) => (
                      <TableRow key={redemption.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{(redemption.profiles as any)?.display_name || 'Unknown'}</p>
                            <p className="text-sm text-muted-foreground">{(redemption.profiles as any)?.email}</p>
                          </div>
                        </TableCell>
                        <TableCell>{(redemption.company_rewards as any)?.name || 'Unknown Reward'}</TableCell>
                        <TableCell>{redemption.points_spent}</TableCell>
                        <TableCell>{format(new Date(redemption.redeemed_at), 'MMM d, yyyy')}</TableCell>
                        <TableCell>{getStatusBadge(redemption.status)}</TableCell>
                        <TableCell>
                          {redemption.status === 'pending' && (
                            <div className="flex gap-1">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => updateRedemptionMutation.mutate({ id: redemption.id, status: 'approved' })}
                              >
                                Approve
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => updateRedemptionMutation.mutate({ id: redemption.id, status: 'rejected' })}
                              >
                                Reject
                              </Button>
                            </div>
                          )}
                          {redemption.status === 'approved' && (
                            <Button 
                              size="sm"
                              onClick={() => updateRedemptionMutation.mutate({ id: redemption.id, status: 'fulfilled' })}
                            >
                              Mark Fulfilled
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
